/**
 * A click becomes a message.
 *
 * The follow-up question (`question` block) shows buttons — candidates, options. A click on one is
 * not a mechanism of its own: it sends the same message the user could have typed, with `planId`
 * and answer, and the run continues normally (UX_CHAT/PLAN.md AP7: "a click is a pre-worded
 * message"). Hence no socket, no special path — just a small module store that the main chat and
 * the questions panel both pick up from, as with `autoNavigate.ts`.
 *
 * Module store instead of jotai, because the block sends from inside the message renderer while
 * the runner sits in the chat container — two trees, one state. Once-only via a key, never via a
 * boolean flag (StrictMode, trap 8 in CHAT_FUNCTIONALITY.md).
 */
import { useEffect, useSyncExternalStore } from 'react';

export const CHAT_SEND_TTL_MS = 10_000;

export interface ChatSendRequest {
    /** The text sent as the user message — exactly as it appears in the transcript. */
    text: string;
    /** The plan the answer belongs to. Only for traceability in the log; the server finds it via the tool call. */
    planId?: string;
    questionKey?: string;
    /** Once-only: the same key is never sent twice. */
    key: string;
    requestedAt: number;
}

let pending: ChatSendRequest | null = null;
const handled = new Set<string>();
const HANDLED_LIMIT = 50;
const listeners = new Set<() => void>();

function emit(): void {
    listeners.forEach((listener) => listener());
}

export function requestChatSend(input: Omit<ChatSendRequest, 'requestedAt'>, now: number = Date.now()): boolean {
    if (!input.text.trim()) return false;
    if (handled.has(input.key)) return false;
    pending = { ...input, requestedAt: now };
    emit();
    return true;
}

export function peekChatSend(now: number = Date.now()): ChatSendRequest | null {
    if (!pending) return null;
    if (now - pending.requestedAt > CHAT_SEND_TTL_MS) {
        pending = null;
        return null;
    }
    return pending;
}

export function consumeChatSend(now: number = Date.now()): ChatSendRequest | null {
    const current = peekChatSend(now);
    if (!current) return null;
    pending = null;
    handled.add(current.key);
    if (handled.size > HANDLED_LIMIT) {
        const first = handled.values().next().value;
        if (first !== undefined) handled.delete(first);
    }
    emit();
    return current;
}

export function resetChatSend(): void {
    pending = null;
    handled.clear();
    emit();
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function useChatSend(): ChatSendRequest | null {
    return useSyncExternalStore(subscribe, () => pending, () => null);
}

/**
 * The picker-up: sits once in the chat container (main chat and panel) and sends the message via
 * the respective submit function. Exactly once per key, even under StrictMode.
 */
export function useChatSendRunner(submit: ((text: string) => void | Promise<unknown>) | undefined): void {
    const request = useChatSend();
    useEffect(() => {
        if (!request || !submit) return;
        const consumed = consumeChatSend();
        if (!consumed) return;
        void submit(consumed.text);
    }, [request, submit]);
}

/** The message for an answer — worded so it reads well in the transcript and the model maps it to the plan. */
export function answerMessage(input: { label: string; planId: string; questionKey: string }): Omit<ChatSendRequest, 'requestedAt'> {
    return {
        text: input.label,
        planId: input.planId,
        questionKey: input.questionKey,
        key: `${input.planId}:${input.questionKey}:${input.label}`,
    };
}
